import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Tooltip, Dropdown, Menu } from "antd";
import { Link } from "react-router-dom";
import EditInfo from "./EditInfo";
import DeleteModal from "./DeleteModal";
import { Avatar_02 } from "../../Routes/ImagePath";
import {
  getMentorList,
  patchMentorStatus,
} from "../../appRedux/actions/mentors";

function ListView({ searchText = "", selectedStatus = "" }) {
  const dispatch = useDispatch();
  const [list, setList] = useState([]);

  const { mentorList, totalRecords, queryInfo } = useSelector((state) => ({
    mentorList: state.mentors.mentorList || [],
    totalRecords: state.mentors.totalRecords || 0,
    queryInfo: state.mentors.queryInfo || {},
  }));

  useEffect(() => {
    let filtered = [...mentorList];

    if (searchText && searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      filtered = filtered.filter(
        (m) =>
          (m.name && m.name.toLowerCase().includes(q)) ||
          (m.displayName && m.displayName.toLowerCase().includes(q)) ||
          (m.email && m.email.toLowerCase().includes(q)) ||
          (m.organization && m.organization.toLowerCase().includes(q)) ||
          (m.designation && m.designation.toLowerCase().includes(q)) ||
          (m.headline && m.headline.toLowerCase().includes(q)) ||
          (m.domains && m.domains.some((d) => d.toLowerCase().includes(q))) ||
          (m.expertiseAreas && m.expertiseAreas.some((e) => e.toLowerCase().includes(q)))
      );
    }

    if (selectedStatus) {
      filtered = filtered.filter((m) => m.status === selectedStatus);
    }

    setList(filtered);
  }, [searchText, selectedStatus, mentorList]);

  const handleStatusToggle = async (mentor, newStatus) => {
    await dispatch(patchMentorStatus(mentor._id, newStatus));
    dispatch(getMentorList(queryInfo));
  };

  const columns = [
    {
      title: "Mentor",
      dataIndex: "name",
      key: "name",
      width: 280,
      render: (text, record) => (
        <div className="d-flex align-items-center">
          <img
            src={record.profileImage || Avatar_02}
            alt={record.name}
            className="rounded-circle me-2"
            style={{
              width: "44px",
              height: "44px",
              objectFit: "cover",
              border: "1px solid #e2e8f0",
              flexShrink: 0,
            }}
          />
          <div style={{ overflow: "hidden" }}>
            <h6 className="mb-0 text-dark fw-bold text-truncate" style={{ fontSize: "14px" }}>
              {record.displayName || record.name}
            </h6>
            {record.displayName && record.displayName !== record.name && (
              <small className="text-secondary d-block text-truncate" style={{ fontSize: "11px" }}>
                Full: {record.name}
              </small>
            )}
            <small className="text-muted d-block text-truncate" style={{ fontSize: "12px" }}>
              {record.email}
            </small>
            {record.headline && (
              <small
                className="text-secondary d-block text-truncate"
                style={{ fontSize: "11px", maxWidth: "220px" }}
                title={record.headline}
              >
                "{record.headline}"
              </small>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Role & Experience",
      key: "role",
      width: 200,
      render: (_, record) => (
        <div>
          <div className="fw-semibold text-dark text-truncate" style={{ fontSize: "13px" }}>
            {record.designation || "Expert"}
          </div>
          <div className="text-muted small text-truncate">
            {record.organization ? `@ ${record.organization}` : "Industry Professional"}
          </div>
          {record.yearsOfExperience > 0 && (
            <span className="badge bg-light text-dark border mt-1" style={{ fontSize: "10px" }}>
              {record.yearsOfExperience}+ Years Exp
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Domains & Skills",
      key: "domains",
      width: 250,
      render: (_, record) => (
        <div>
          <div className="d-flex flex-wrap gap-1 mb-1">
            {(record.domains || []).slice(0, 3).map((domain, idx) => (
              <Tag color="blue" key={idx} style={{ fontSize: "11px", margin: "2px" }}>
                {domain}
              </Tag>
            ))}
            {(record.domains || []).length > 3 && (
              <Tooltip title={record.domains.slice(3).join(", ")}>
                <Tag style={{ fontSize: "11px", margin: "2px" }}>
                  +{record.domains.length - 3} more
                </Tag>
              </Tooltip>
            )}
          </div>
          {record.expertiseAreas && record.expertiseAreas.length > 0 && (
            <div className="d-flex flex-wrap gap-1">
              {record.expertiseAreas.slice(0, 2).map((skill, idx) => (
                <Tag color="cyan" key={idx} style={{ fontSize: "10px", margin: "1px" }}>
                  {skill}
                </Tag>
              ))}
              {record.expertiseAreas.length > 2 && (
                <Tooltip title={record.expertiseAreas.slice(2).join(", ")}>
                  <Tag style={{ fontSize: "10px", margin: "1px" }}>
                    +{record.expertiseAreas.length - 2}
                  </Tag>
                </Tooltip>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Credentials",
      key: "credentials",
      width: 160,
      align: "center",
      render: (_, record) => {
        const creds = record.credentials || [];
        if (creds.length === 0) {
          return <span className="text-muted small">None listed</span>;
        }

        const tooltipContent = (
          <div>
            {creds.map((c, i) => (
              <div key={i} className="mb-1">
                <strong>[{c.type || "Cert"}]</strong> {c.title}{" "}
                {c.institution && `(${c.institution})`}
                {c.year && ` - ${c.year}`}
              </div>
            ))}
          </div>
        );

        return (
          <Tooltip title={tooltipContent}>
            <span
              className="badge bg-info text-dark"
              style={{ cursor: "pointer", fontSize: "11px" }}
            >
              <i className="fa fa-certificate me-1" />
              {creds.length} {creds.length === 1 ? "Credential" : "Credentials"}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      align: "center",
      render: (status, record) => {
        let color = "default";
        let label = status || "DRAFT";

        if (status === "ACTIVE") {
          color = "green";
          label = "Active";
        } else if (status === "DRAFT") {
          color = "gold";
          label = "Draft";
        } else if (status === "INACTIVE") {
          color = "red";
          label = "Inactive";
        }

        return (
          <Dropdown
            overlay={
              <Menu>
                {status !== "ACTIVE" && (
                  <Menu.Item
                    key="active"
                    onClick={() => handleStatusToggle(record, "ACTIVE")}
                  >
                    <span className="text-success">
                      <i className="fa fa-check-circle me-1" /> Mark as Active
                    </span>
                  </Menu.Item>
                )}
                {status !== "DRAFT" && (
                  <Menu.Item
                    key="draft"
                    onClick={() => handleStatusToggle(record, "DRAFT")}
                  >
                    <span className="text-warning">
                      <i className="fa fa-pencil-square-o me-1" /> Set to Draft
                    </span>
                  </Menu.Item>
                )}
                {status !== "INACTIVE" && (
                  <Menu.Item
                    key="inactive"
                    onClick={() => handleStatusToggle(record, "INACTIVE")}
                  >
                    <span className="text-danger">
                      <i className="fa fa-ban me-1" /> Deactivate
                    </span>
                  </Menu.Item>
                )}
              </Menu>
            }
            trigger={["click"]}
          >
            <Tag
              color={color}
              style={{ cursor: "pointer", fontWeight: "bold", padding: "2px 8px" }}
            >
              {label} <i className="fa fa-caret-down ms-1" />
            </Tag>
          </Dropdown>
        );
      },
    },
    {
      title: "Links",
      key: "links",
      width: 80,
      align: "center",
      render: (_, record) => (
        <div className="d-flex justify-content-center gap-2">
          {record.linkedinUrl && (
            <a
              href={record.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary"
              title="LinkedIn Profile"
            >
              <i className="fa fa-linkedin" style={{ fontSize: "16px" }} />
            </a>
          )}
          {record.websiteUrl && (
            <a
              href={record.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondary"
              title="Website / Portfolio"
            >
              <i className="fa fa-globe" style={{ fontSize: "16px" }} />
            </a>
          )}
          {!record.linkedinUrl && !record.websiteUrl && (
            <span className="text-muted">—</span>
          )}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      align: "center",
      render: (text, record) => (
        <div className="dropdown dropdown-action text-center">
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            <EditInfo info={record} key={`edit_info_${record._id}`} />
            <DeleteModal info={record} key={`delete_modal_${record._id}`} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="row m-0">
      <div className="col-md-12 p-0" id="table-responsive">
        <div className="table-responsive" id="setMinheight">
          <Table
            className="table-striped"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
            }}
            style={{ overflowX: "auto" }}
            columns={columns}
            dataSource={list}
            rowKey={(record) => record._id || record.id}
          />
        </div>
      </div>
    </div>
  );
}

export default ListView;
